import {
  AfterViewInit,
  Component,
  computed,
  HostBinding,
  inject,
  input,
  InputSignal,
  OnDestroy,
  signal,
  Signal,
  TemplateRef,
  WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  ControlContainer,
  FormControlStatus,
  FormGroupDirective,
  NgControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { IGNORE_BITWARDEN_AUTOFILL } from './bitwarden-autofill.token';
import { VALUE_ACCESSOR } from './valueAccessor';
import { Subscription } from 'rxjs';
import { CorporateDesignService } from '../../../services/corporate-design/corporate-design.service';
import { ICorporateDesign } from '@web/dtos';
import { WebTextStore } from '../../../services/api/text/web-text-store.service';
import { SiteContextService } from '../../../services/site-context/site-context.service';

@Component({
  selector: 'aw-form-input',
  imports: [CommonModule],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
  template: '',
})
export class FormInputComponent implements AfterViewInit, OnDestroy {
  protected readonly ignoreBitwardenAutofill: boolean = inject(
    IGNORE_BITWARDEN_AUTOFILL
  );
  public isDisabled: InputSignal<boolean> = input(false);
  protected corporateDesignService: CorporateDesignService = inject(
    CorporateDesignService
  );
  protected readonly siteContextService: SiteContextService =
    inject(SiteContextService);
  public corporateDesign: Signal<ICorporateDesign> =
    this.corporateDesignService.corporateDesign;
  public darkModeLabelColor: InputSignal<string> = input<string>(
    this.corporateDesign().colorAdmixtures.dark_text
  );
  public isManualDarkMode: InputSignal<boolean | undefined> = input<
    boolean | undefined
  >(undefined);
  public formControlName: InputSignal<string | undefined> = input<string>();
  public label: InputSignal<string | undefined> = input<string>();
  public placeholder: InputSignal<string | undefined> = input<string>();
  public id: InputSignal<string> = input.required<string>();
  public errorTemplate: InputSignal<TemplateRef<unknown> | undefined> =
    input<TemplateRef<unknown>>();

  protected textStore: WebTextStore = inject(WebTextStore);

  protected subscription: Subscription = new Subscription();
  public isFocused: WritableSignal<boolean> = signal(false);
  public isValid: WritableSignal<boolean> = signal(false);
  public updateStyling: WritableSignal<string> = signal('');

  @HostBinding('class') get formInputClasses(): string {
    return 'w-full';
  }

  public formattedLabel: Signal<string | undefined> = computed(() => {
    const label: string | undefined = this.label();
    return label ? this.markFieldIfRequired(label) : undefined;
  });

  public borderRounding: Signal<string> = computed(() => {
    if (this.siteContextService.isShopContext()) {
      return this.corporateDesign().buttonBorderRounding + 'px';
    }
    return '';
  });

  public borderAndLabelColor: Signal<string> = computed(() => {
    this.updateStyling();
    if (this.siteContextService.isShopContext()) {
      return this.isFocused()
        ? this.corporateDesignService.linkIconColor()
        : this.isDarkMode()
          ? this.ngControl.errors && this.ngControl.touched
            ? ''
            : this.corporateDesign().primaryDarkFontColor
          : '';
    }
    return this.isDarkMode() ? this.darkModeLabelColor() : '';
  });

  public inputBackground: Signal<string> = computed(() => {
    return (this.siteContextService.isShopContext() && this.isDarkMode()) ||
      this.isManualDarkMode()
      ? this.corporateDesignService.corporateDesign().colorAdmixtures
          .dark_surface_01
      : '#FFFFFF';
  });

  protected isDarkMode() {
    if (this.isManualDarkMode() !== undefined) {
      return this.isManualDarkMode();
    }
    return this.corporateDesignService.isDarkModeActive();
  }

  public inputSelectedColor: Signal<string> = computed(() => {
    if (this.siteContextService.isShopContext()) {
      return this.isValid() ? this.corporateDesignService.primaryColor() : '';
    }
    return '';
  });

  public inputContrastColor: Signal<string> = computed(() => {
    if (!this.siteContextService.isShopContext()) {
      return '';
    }
    return this.corporateDesignService.primaryFontColor();
  });

  constructor(public ngControl: NgControl) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = VALUE_ACCESSOR;
    }
  }

  ngAfterViewInit(): void {
    this.ngControl.control!.addValidators(this.usesOnlyValidCharacters());
    this.subscription.add(
      this.ngControl.control!.statusChanges.subscribe(
        (status: FormControlStatus) => {
          this.isValid.set(status === 'VALID');
        }
      )
    );
    this.isValid.set(this.ngControl.control!.valid);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.ngControl.control!.removeValidators(this.usesOnlyValidCharacters());
  }

  private markFieldIfRequired(label: string) {
    if (
      this.ngControl.control!.hasValidator(Validators.required) ||
      this.ngControl.control!.hasValidator(Validators.requiredTrue)
    ) {
      return label + '*';
    }
    return label;
  }

  private usesOnlyValidCharacters(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value === undefined || control.value === null) {
        return null;
      }
      const inputValue: string = JSON.stringify(control.value);
      //currently german, may need expansion/adjustment later
      const latin_1_additional =
        '¡¢£¤¥¦§¨©ª«¬®¯°±²³´µ¶·¸¹º»¼½¾¿ÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖ×ØÙÚÛÜÝÞß';
      const latin_1_additional_lower = 'àáâãäåæçèéêëìíîïðñòóôõö÷øùúûüýþÿ';
      const latin_2_additional =
        'Ą˘Ł¤ĽŚ§¨ŠŞŤŹŽŻ°ą˛ł´ľśˇ¸šşťź˝žżŔÁÂĂÄĹĆÇČÉĘËĚÍÎĎĐŃŇÓÔŐÖ×ŘŮÚŰÜÝŢß';
      const latin_2_additional_lower = 'ŕáâăäĺćçčéęëěíîďđńňóôő÷řůúűüýţ˙';

      const checkForInvalidChars = new RegExp(
        //eslint-disable-next-line
        `[^a-zA-Z0-9\\sÄÖÜäöüß().:,;@\^?!'#~|\`\´"\\[\\]\\{\\}\\/\\\\<>\\$€=§%&+*\_\\-${latin_1_additional}${latin_1_additional_lower}${latin_2_additional}${latin_2_additional_lower}]`
      );
      return checkForInvalidChars.test(inputValue)
        ? { characterValidity: checkForInvalidChars.exec(inputValue) }
        : null;
    };
  }
}
